import { useEffect, useRef, useState } from 'react';
import Agendamentos from '../../services/Agendamentos';
import { buildOccurrences, isValidDate, isValidTime } from '../../utilities/agendamentoSeries';

const CHECK_IDLE = { state: 'idle', conflict: null, message: null };

// Motivo que impede salvar, escrito ao lado do botão. O aviso do paciente (PACIENTE_SAME_DAY) não impede
const BLOCKING_TEXT = {
  OUTSIDE_SERVICE_HOURS: 'Fora do horário do doutor: mude a data ou o horário.',
  SCHEDULE_CONFLICT: 'Horário ocupado na agenda do doutor: mude a data ou o horário.',
  ALREADY_SCHEDULED: 'Este paciente já tem este agendamento.',
};

// O agendamento novo: toda recusa impede salvar
const ENFORCE_ALL = { serviceHours: true, slot: true };

// Agendamento sem repetição (novo ou aberto): confere a agenda do doutor com a mesma verificação da lista do lote
// (uma sessão só), meio segundo depois da última mudança e sem gravar. No aberto, agendamentoId o deixa fora da conferência,
// e enforce diz quais recusas impedem salvar (o servidor só recusa o que muda); as outras ficam como aviso
export function useAgendamentoCheck({ enabled, tecnicoId, pacienteId, agendamentoId, date, startTime, endTime, enforce = ENFORCE_ALL }) {
  const [check, setCheck] = useState(CHECK_IDLE);
  const sequence = useRef(0);

  // Mesmas bordas da lista do lote: o backend recusa o início 00:00 (o modal abre com 00:00)
  const complete = isValidDate(date) && isValidTime(startTime) && startTime !== '00:00' && isValidTime(endTime) && endTime > startTime;

  useEffect(() => {
    const mine = ++sequence.current;

    if (!enabled || !complete) {
      setCheck(CHECK_IDLE);
      return undefined;
    }

    if (!tecnicoId) {
      setCheck({ ...CHECK_IDLE, message: 'Escolha o doutor para conferir a agenda.' });
      return undefined;
    }

    setCheck({ state: 'checking', conflict: null, message: null });

    const timer = setTimeout(() => {
      const occurrences = buildOccurrences([{ date, start_time: startTime, end_time: endTime }]);

      Agendamentos.checkBatch({ tecnico_id: tecnicoId, paciente_id: pacienteId || undefined, agendamento_id: agendamentoId || undefined, occurrences })
        .then(({ data }) => {
          if (mine === sequence.current) setCheck({ state: 'ready', conflict: data.conflicts.find((conflict) => conflict.index === 0) ?? null, message: null });
        })
        .catch((error) => {
          if (mine !== sequence.current) return;

          // 4xx: o servidor diz o que recusou; sem resposta: falha de rede
          const message = error?.response?.data?.message ?? 'Não foi possível verificar agora. A agenda é conferida de novo ao salvar.';
          setCheck({ state: 'failed', conflict: null, message });
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [enabled, complete, tecnicoId, pacienteId, agendamentoId, date, startTime, endTime]);

  const conflict = check.state === 'ready' ? check.conflict : null;
  const enforced = conflict?.blocking && (conflict.reason === 'OUTSIDE_SERVICE_HOURS' ? enforce.serviceHours
    : (['SCHEDULE_CONFLICT', 'ALREADY_SCHEDULED'].includes(conflict.reason) ? enforce.slot : true));
  const blocking = enforced ? (BLOCKING_TEXT[conflict.reason] ?? conflict.message) : null;

  return { ...check, blocking };
}
